<script setup>
// Informes › Redes (Victor 2026-06-02 · Propuesta 1 "Dashboard ejecutivo").
// Pantalla completa, 4 KPIs (incl. Visualizaciones 30d), tabla ancha con la
// FOTO DE PERFIL real de cada cuenta (IG profile_picture_url / FB picture).
// Visualizaciones 30d se muestra "—" hasta habilitar permisos de Meta Insights.
import { ref, reactive, computed, onMounted } from 'vue';
import SocialStatsAPI from 'dashboard/api/socialStats';

const loading = ref(true);
const refreshing = ref(false);
const rows = ref([]);
const totals = ref({ followers: 0, posts: 0, accounts: 0, views_30d: 0 });
const lastUpdated = ref(null);
const broken = reactive({}); // avatares que fallaron al cargar

const fmt = n => (n ?? 0).toLocaleString('es-DO');

const netMeta = network => {
  if (network === 'instagram') return { label: 'Instagram', cls: 'ig', icon: '📷' };
  if (network === 'facebook') return { label: 'Facebook', cls: 'fb', icon: 'f' };
  return { label: network, cls: 'other', icon: '•' };
};

const lastUpdatedText = computed(() => {
  if (!lastUpdated.value) return 'Sin datos aún';
  const d = new Date(lastUpdated.value);
  return `Actualizado: ${d.toLocaleString('es-DO', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  })}`;
});

const apply = data => {
  rows.value = data.rows || [];
  totals.value = data.totals || { followers: 0, posts: 0, accounts: 0, views_30d: 0 };
  lastUpdated.value = data.last_updated || null;
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await SocialStatsAPI.get();
    apply(data);
  } catch (_) {
    /* silencioso */
  } finally {
    loading.value = false;
  }
};

const refresh = async () => {
  if (refreshing.value) return;
  refreshing.value = true;
  try {
    const { data } = await SocialStatsAPI.refresh();
    apply(data);
  } catch (_) {
    /* silencioso */
  } finally {
    refreshing.value = false;
  }
};

// Clic en la cuenta → abre el perfil. PC: pestaña del navegador. Celular:
// intenta la app de Instagram (deep link) y si no, cae al sitio web.
const openProfile = r => {
  if (!r.web_url) return;
  const isMobile = window.innerWidth < 768;
  if (isMobile && r.app_url) {
    window.location.href = r.app_url;
    setTimeout(() => {
      window.location.href = r.web_url;
    }, 700);
  } else {
    window.open(r.web_url, '_blank', 'noopener');
  }
};

const avatarKey = r => `${r.network}:${r.username}`;
const showAvatar = r => r.avatar_url && !broken[avatarKey(r)];
const onImgError = r => {
  broken[avatarKey(r)] = true;
};

const viewsText = v => (v ? fmt(v) : '—');

// === Móvil · Propuesta C (Victor 2026-06-03): filtro por plataforma + barra
// dorada de fondo en cada fila (la fila "se llena" según seguidores). En PC se
// mantiene la tabla; esto solo aplica en celular vía CSS.
const platform = ref('all'); // 'all' | 'instagram' | 'facebook'
const filteredRows = computed(() => {
  const list =
    platform.value === 'all'
      ? rows.value
      : rows.value.filter(r => r.network === platform.value);
  return [...list].sort((a, b) => (b.followers || 0) - (a.followers || 0));
});
const maxFollowers = computed(
  () => filteredRows.value.reduce((m, r) => Math.max(m, r.followers || 0), 0) || 1
);
// Ancho de la barra relativo a la cuenta más grande del filtro (top = 100%).
const barPct = r =>
  Math.max(3, Math.round(((r.followers || 0) / maxFollowers.value) * 100));
const mobileSummary = computed(() => {
  const list = filteredRows.value;
  const foll = list.reduce((s, r) => s + (r.followers || 0), 0);
  const net =
    platform.value === 'instagram'
      ? 'IG'
      : platform.value === 'facebook'
        ? 'FB'
        : '';
  return `${fmt(foll)} seguidores · ${list.length} cuentas ${net}`.trim();
});

onMounted(load);
</script>

<template>
  <div class="sr-root">
    <!-- Header -->
    <div class="sr-head">
      <div class="sr-titlewrap">
        <div class="sr-ico">📡</div>
        <div>
          <div class="sr-title">Centro de Redes</div>
          <div class="sr-sub">Seguidores y visualizaciones por cuenta · {{ lastUpdatedText }}</div>
        </div>
      </div>
      <button class="sr-refresh" :disabled="refreshing" @click="refresh">
        <span v-if="refreshing">Actualizando…</span>
        <span v-else>↻ Actualizar</span>
      </button>
    </div>

    <!-- KPIs (4) -->
    <div class="sr-kpis">
      <div class="sr-kpi">
        <div class="l">Seguidores totales</div>
        <div class="v gold">{{ fmt(totals.followers) }}</div>
      </div>
      <div class="sr-kpi">
        <div class="l">Visualizaciones 30d</div>
        <div class="v">{{ viewsText(totals.views_30d) }}</div>
        <div class="hint">requiere permisos de Insights</div>
      </div>
      <div class="sr-kpi">
        <div class="l">Cuentas activas</div>
        <div class="v">{{ totals.accounts }}</div>
      </div>
      <div class="sr-kpi">
        <div class="l">Publicaciones</div>
        <div class="v">{{ fmt(totals.posts) }}</div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="sr-card">
      <div v-if="loading" class="sr-empty">Cargando seguidores…</div>
      <div v-else-if="!rows.length" class="sr-empty">
        Sin cuentas conectadas o sin datos. Toca “Actualizar”.
      </div>
      <div v-else class="sr-table-scroll">
        <table class="sr-table">
          <thead>
            <tr>
              <th class="l">CUENTA</th>
              <th>RED</th>
              <th class="r">SEGUIDORES</th>
              <th class="r">VISUALIZACIONES 30D</th>
              <th class="r">POSTS</th>
              <th class="r">CREC. 30D</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in rows"
              :key="i"
              :class="{ 'sr-clickable': r.web_url }"
              @click="openProfile(r)"
            >
              <td class="l">
                <div class="sr-acc">
                  <span class="sr-avatar">
                    <img
                      v-if="showAvatar(r)"
                      :src="r.avatar_url"
                      alt=""
                      referrerpolicy="no-referrer"
                      @error="onImgError(r)"
                    />
                    <span v-else class="sr-net" :class="netMeta(r.network).cls">{{ netMeta(r.network).icon }}</span>
                    <span class="sr-badge" :class="netMeta(r.network).cls">{{ netMeta(r.network).icon }}</span>
                  </span>
                  <span class="sr-han">{{ r.username || '—' }}</span>
                  <span v-if="r.web_url" class="sr-arrow">↗</span>
                </div>
              </td>
              <td>{{ r.network_label }}</td>
              <td class="r sr-foll">{{ fmt(r.followers) }}</td>
              <td class="r sr-views">{{ viewsText(r.views_30d) }}</td>
              <td class="r">{{ r.posts ? fmt(r.posts) : '—' }}</td>
              <td class="r">
                <span
                  v-if="r.growth_pct !== null && r.growth_pct !== undefined"
                  :class="r.growth_pct >= 0 ? 'up' : 'dn'"
                >{{ r.growth_pct >= 0 ? '▲' : '▼' }} {{ Math.abs(r.growth_pct) }}%</span>
                <span v-else class="dim">—</span>
              </td>
            </tr>
            <tr class="sr-total">
              <td class="l">TOTAL</td>
              <td>{{ totals.accounts }} cuentas</td>
              <td class="r">{{ fmt(totals.followers) }}</td>
              <td class="r">{{ viewsText(totals.views_30d) }}</td>
              <td class="r">{{ fmt(totals.posts) }}</td>
              <td class="r">—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Móvil · Propuesta C: filtro por plataforma + barra dorada (solo celular) -->
    <div class="sr-mobile">
      <div class="sr-tabs">
        <button
          :class="{ on: platform === 'instagram' }"
          @click="platform = 'instagram'"
        >📷 Instagram</button>
        <button
          :class="{ on: platform === 'facebook' }"
          @click="platform = 'facebook'"
        >👍 Facebook</button>
        <button :class="{ on: platform === 'all' }" @click="platform = 'all'">
          Todas
        </button>
      </div>
      <div class="sr-msum">{{ mobileSummary }}</div>
      <div v-if="loading" class="sr-empty">Cargando seguidores…</div>
      <div v-else-if="!filteredRows.length" class="sr-empty">
        Sin cuentas en esta red.
      </div>
      <template v-else>
        <div
          v-for="(r, i) in filteredRows"
          :key="`m${i}`"
          class="sr-mrow"
          :class="{ 'sr-clickable': r.web_url }"
          @click="openProfile(r)"
        >
          <span class="sr-mfill" :style="{ width: barPct(r) + '%' }"></span>
          <span class="sr-avatar sr-mav">
            <img
              v-if="showAvatar(r)"
              :src="r.avatar_url"
              alt=""
              referrerpolicy="no-referrer"
              @error="onImgError(r)"
            />
            <span v-else class="sr-net" :class="netMeta(r.network).cls">{{ netMeta(r.network).icon }}</span>
            <span class="sr-badge" :class="netMeta(r.network).cls">{{ netMeta(r.network).icon }}</span>
          </span>
          <span class="sr-mname">{{ r.username || '—' }}</span>
          <span class="sr-mfoll">{{ fmt(r.followers) }}</span>
        </div>
      </template>
    </div>

    <div class="sr-note">
      Instagram y Facebook se actualizan automáticamente cada ~6 h. El crecimiento
      30d se calcula al acumular datos. Las visualizaciones 30d aparecen al
      habilitar los permisos de Meta Insights. TikTok: próximamente.
    </div>
  </div>
</template>

<style scoped>
.sr-root {
  --bg-0:#0a0b0d; --bg-1:#0e1014; --card:#15171c; --card2:#1a1d23;
  --bd:rgba(255,255,255,.07); --w55:rgba(255,255,255,.5); --w35:rgba(255,255,255,.32);
  --gold:#c9a35a; --green:#46c98a; --red:#d4a8a8;
  padding: 22px 28px 60px;
  color: #e8e9ed;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  width: 100%;
}
.sr-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px; margin-bottom: 22px; flex-wrap: wrap;
}
.sr-titlewrap { display: flex; align-items: center; gap: 13px; }
.sr-ico {
  width: 46px; height: 46px; border-radius: 13px; flex: none;
  background: linear-gradient(135deg,#2a2d34,#15171b);
  border: 1px solid rgba(201,163,90,.3);
  display: grid; place-items: center; font-size: 21px;
}
.sr-title { font-size: 21px; font-weight: 800; color: #fff; letter-spacing: .2px; }
.sr-sub { font-size: 12px; color: var(--w55); margin-top: 3px; }
.sr-refresh {
  padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 800;
  background: var(--gold); color: #1a1610; border: none; cursor: pointer;
}
.sr-refresh:disabled { opacity: .6; cursor: default; }

.sr-kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 22px;
}
.sr-kpi {
  background: linear-gradient(160deg,var(--card2),var(--card));
  border: 1px solid var(--bd); border-radius: 18px; padding: 20px 22px;
  position: relative; overflow: hidden;
}
.sr-kpi::after {
  content: ''; position: absolute; right: -30px; top: -30px; width: 120px; height: 120px;
  border-radius: 50%; background: radial-gradient(circle, rgba(201,163,90,.12), transparent 70%);
}
.sr-kpi .l { font-size: 10px; letter-spacing: .18em; color: var(--w35); font-weight: 800; text-transform: uppercase; }
.sr-kpi .v { font-size: 34px; font-weight: 300; margin-top: 8px; font-variant-numeric: tabular-nums; letter-spacing: -.5px; }
.sr-kpi .v.gold { color: var(--gold); }
.sr-kpi .hint { font-size: 10px; color: var(--w35); margin-top: 5px; }

.sr-card { background: var(--card); border: 1px solid var(--bd); border-radius: 18px; overflow: hidden; }
.sr-empty { padding: 60px 20px; text-align: center; color: var(--w55); font-size: 13px; }
.sr-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.sr-table { width: 100%; border-collapse: collapse; font-size: 13.5px; min-width: 720px; }
.sr-table th {
  font-size: 9.5px; letter-spacing: .13em; color: var(--w35); font-weight: 800;
  text-align: right; padding: 15px 18px; border-bottom: 1px solid var(--bd); white-space: nowrap;
  background: var(--bg-1);
}
.sr-table th.l { text-align: left; } .sr-table th.r { text-align: right; }
.sr-table td {
  padding: 13px 18px; border-bottom: 1px solid rgba(255,255,255,.04);
  text-align: right; font-variant-numeric: tabular-nums; color: var(--w55); white-space: nowrap;
}
.sr-table td.l { text-align: left; color: #e8e9ed; } .sr-table td.r { text-align: right; }
.sr-acc { display: flex; align-items: center; gap: 12px; }
.sr-avatar { position: relative; width: 38px; height: 38px; flex: none; }
.sr-avatar img {
  width: 38px; height: 38px; border-radius: 50%; object-fit: cover;
  border: 1px solid var(--bd); display: block;
}
.sr-net {
  width: 38px; height: 38px; border-radius: 50%; color: #fff; font-weight: 800;
  display: grid; place-items: center; font-size: 16px;
}
.sr-net.ig, .sr-badge.ig { background: linear-gradient(135deg,#feda75,#d62976,#962fbf,#4f5bd5); }
.sr-net.fb, .sr-badge.fb { background: #1877f2; }
.sr-net.other, .sr-badge.other { background: #333; }
.sr-badge {
  position: absolute; right: -3px; bottom: -3px; width: 17px; height: 17px; border-radius: 50%;
  display: grid; place-items: center; font-size: 9px; font-weight: 800; color: #fff;
  border: 2px solid var(--card);
}
.sr-han { font-weight: 700; color: #e8e9ed; }
.sr-arrow { color: var(--gold); margin-left: 4px; opacity: .45; transition: opacity .12s; font-weight: 700; }
.sr-clickable { cursor: pointer; transition: background .12s; }
.sr-clickable:hover { background: rgba(201,163,90,.05); }
.sr-clickable:hover .sr-arrow { opacity: 1; }
.sr-clickable:hover .sr-han { color: var(--gold); }
.sr-foll { font-weight: 800; color: #fff; }
.sr-views { font-weight: 800; color: var(--gold); }
.up { color: var(--green); font-weight: 700; } .dn { color: var(--red); } .dim { color: var(--w35); }
.sr-total td { background: rgba(201,163,90,.06); color: var(--gold); font-weight: 800; border-top: 1px solid rgba(201,163,90,.2); }

.sr-note { font-size: 11px; color: var(--w35); margin-top: 14px; line-height: 1.5; }

/* === Móvil · Propuesta C: filtro por plataforma + barra dorada en la fila === */
.sr-mobile { display: none; }
.sr-tabs {
  display: flex; gap: 6px; background: rgba(255,255,255,.04);
  border: 1px solid var(--bd); border-radius: 12px; padding: 4px; margin-bottom: 12px;
}
.sr-tabs button {
  flex: 1; border: none; background: transparent; color: var(--w55);
  font-size: 12px; font-weight: 700; padding: 9px 4px; border-radius: 9px; cursor: pointer;
}
.sr-tabs button.on {
  background: linear-gradient(180deg,#26262d,#1a1a1f); color: var(--gold);
  box-shadow: 0 1px 0 rgba(255,255,255,.05);
}
.sr-msum { font-size: 11px; color: var(--w35); margin: 0 2px 12px; letter-spacing: .04em; }
.sr-mrow {
  position: relative; height: 50px; border-radius: 12px; margin-bottom: 8px;
  overflow: hidden; border: 1px solid rgba(255,255,255,.04);
  background: rgba(255,255,255,.015); display: flex; align-items: center;
  padding: 0 14px; gap: 11px;
}
.sr-mfill {
  position: absolute; left: 0; top: 0; bottom: 0; z-index: 0;
  background: linear-gradient(90deg, rgba(201,163,90,.30), rgba(201,163,90,.07));
  border-right: 2px solid var(--gold);
}
.sr-mav { z-index: 2; }
.sr-mav, .sr-mav img, .sr-mav .sr-net { width: 30px !important; height: 30px !important; }
.sr-mav .sr-net { font-size: 13px; }
.sr-mname {
  z-index: 2; flex: 1; min-width: 0; font-size: 12.5px; font-weight: 700;
  color: #e8e9ed; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sr-mfoll { z-index: 2; font-size: 13.5px; font-weight: 800; color: #e7cf9a; font-variant-numeric: tabular-nums; }

@media (max-width: 767px) {
  .sr-root { padding: 16px; }
  .sr-kpis { display: none; }
  .sr-card { display: none; }
  .sr-mobile { display: block; }
}
</style>
